/*
 * Modal — behaviour for the theme's reusable overlay panel.
 *
 * Markup comes from snippets/modal.liquid; styling from assets/modal.css, whose comment block
 * explains why this is a native <dialog> opened with showModal() rather than a fixed-position div.
 *
 * Loaded once, globally, with `defer` from snippets/theme-scripts.liquid. Everything below is
 * delegated off `document`, so there is deliberately NO per-modal initialisation:
 *
 *   - A modal added to the page by a theme-editor section re-render works immediately, with no
 *     `shopify:section:load` rebinding and no risk of stacking duplicate handlers the way a
 *     per-instance binder would.
 *   - A page with ten modals costs exactly the same three listeners as a page with one.
 *
 * showModal() gives focus trapping, focus restore to the trigger, ESC-to-dismiss and an inert
 * background for free. None of that is reimplemented here — the only reason `cancel` and `close`
 * are touched at all is to play the exit animation and to release the scroll lock.
 */
(function () {
  var OPEN_ATTR = 'data-modal-open';
  var CLOSE_ATTR = 'data-modal-close';
  var CLOSING_ATTR = 'data-modal-closing';
  var HASH_PREFIX = '#Modal-';
  var LOCK_CLASS = 'modal-open';

  var supportsDialog =
    typeof window.HTMLDialogElement === 'function' &&
    typeof window.HTMLDialogElement.prototype.showModal === 'function';

  function prefersReducedMotion() {
    return window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  }

  function getDialog(id) {
    if (!id) return null;
    /* getElementById rather than a selector, so an id containing characters that would need
       escaping in querySelector (a section id is not guaranteed to be CSS-safe) still resolves. */
    var el = document.getElementById('Modal-' + id);
    return el && el.tagName === 'DIALOG' ? el : null;
  }

  /* ---- Scroll lock ----
     The page behind a modal must not scroll. <dialog> does not do this on its own. */
  function lockScroll() {
    var root = document.documentElement;
    if (root.classList.contains(LOCK_CLASS)) return;

    /* Hiding the scrollbar reclaims its width, which would shunt the whole page sideways as the
       modal opens. Reserving that width on <body> keeps everything still. Zero on overlay-scrollbar
       platforms (macOS, touch), where there is nothing to compensate for. */
    var gap = window.innerWidth - root.clientWidth;
    if (gap > 0) {
      document.body.style.paddingRight = gap + 'px';
    }
    root.classList.add(LOCK_CLASS);
  }

  function unlockScroll() {
    /* Only the last modal standing releases the lock. Two open at once is not a pattern this
       component encourages, but a stacked confirm-over-panel must not leave the page scrollable. */
    if (document.querySelector('dialog.modal[open]')) return;
    document.documentElement.classList.remove(LOCK_CLASS);
    document.body.style.paddingRight = '';
  }

  function open(dialog) {
    if (!dialog || dialog.hasAttribute('open')) return;
    dialog.removeAttribute(CLOSING_ATTR);

    /* BEFORE the dialog opens, not after. lockScroll writes padding-right on <body>, which
       reflows the page; doing that after showModal lands it on the very frame the opening
       animation starts, and the drawer visibly jumps sideways as it slides. Locking first lets
       the reflow settle while there is still nothing on screen to see it.

       Safe to move: lockScroll measures the scrollbar gap and only then applies the overflow
       lock, so the measurement is the same either side of showModal. Invisible on macOS and
       touch, where overlay scrollbars make the gap zero — this is for Windows, Linux, and a Mac
       set to always show scrollbars. */
    lockScroll();

    if (supportsDialog) {
      /* Guarded only because the lock now goes on first. showModal() throws if the dialog is not
         connected to the document — getDialog() resolves it fresh, so that should not happen, but
         an unhandled throw here would leave the page locked with nothing open and no route back.
         Failing to open a panel is recoverable; an unscrollable page is not. */
      try {
        dialog.showModal();
      } catch (e) {
        unlockScroll();
        return;
      }
      /* Re-focus the panel with the scroll suppressed. showModal() has already moved focus, and
         whatever it landed on it did so with scroll-into-view enabled — which, while the drawer
         panel is still parked off-screen at translateX(100%), is what asks the browser to scroll
         sideways after it. modal.css kills the scroll container so there is nothing to scroll;
         this removes the request itself, so the two fixes do not depend on each other (Safari
         15.4-15.6 has showModal() but not `overflow: clip`).
         It also pins down WHICH element ends up focused. modal.css documents the panel as the
         target — a screen reader should open on the title, not on "Close" — but engines disagree
         about what showModal() focuses when nothing carries autofocus. Being explicit settles it.
         Focus restore is unaffected: the dialog was opened modally, so close() still returns
         focus to the trigger. */
      var opened = dialog.querySelector('.modal__panel');
      if (opened) opened.focus({ preventScroll: true });
    } else {
      /* No top layer here — modal.css positions .modal--fallback by hand. */
      dialog.classList.add('modal--fallback');
      dialog.setAttribute('open', '');
      var panel = dialog.querySelector('.modal__panel');
      if (panel) panel.focus();
    }
  }

  function close(dialog) {
    if (!dialog || !dialog.hasAttribute('open')) return;

    function finish() {
      dialog.removeAttribute(CLOSING_ATTR);
      if (supportsDialog) {
        /* Also hands focus back to whatever opened the panel, because it was opened modally. */
        dialog.close();
      } else {
        dialog.removeAttribute('open');
        dialog.classList.remove('modal--fallback');
      }
      /* Released here rather than left to the `close` event below. That event is queued as a task,
         not dispatched synchronously, so anything that runs before the queue drains — the very
         next line of a caller's code, a timer scheduled in the same tick — would still see a
         locked page. The listener stays as the catch-all for a dialog.close() called from outside
         this file; unlockScroll is idempotent, so running twice costs nothing. */
      unlockScroll();
    }

    if (prefersReducedMotion()) {
      finish();
      return;
    }

    dialog.setAttribute(CLOSING_ATTR, '');

    var panel = dialog.querySelector('.modal__panel');
    if (!panel) {
      finish();
      return;
    }

    /* Driven by the animation actually ending rather than by a hardcoded duration, so retiming the
       keyframes in modal.css never leaves the panel visibly snapping or lingering. The timeout is
       a backstop for the case where no animation runs at all (a display change mid-close, a
       stylesheet that failed to load) — without it the panel would stick open for good.

       It has to stay comfortably LONGER than the slowest exit animation in modal.css, or it stops
       being a backstop and becomes the thing that ends the close — firing mid-slide and snapping
       the panel away. The drawer's exit is --dur-drawer, currently 380ms, and it matches the
       theme's own side drawers by design. 800ms leaves better than 2x headroom.

       Deliberately not read from the computed style. The value this guards against is the case
       where the animation never runs, which is exactly the case where measuring it tells you
       nothing useful — a generous constant is the more honest instrument. Raise it if the drawer
       exit in modal.css and the vendor retune in motion.css ever get slower together. */
    var done = false;
    function onEnd(event) {
      if (event && event.target !== panel) return;
      if (done) return;
      done = true;
      panel.removeEventListener('animationend', onEnd);
      finish();
    }
    panel.addEventListener('animationend', onEnd);
    window.setTimeout(onEnd, 800);
  }

  /* ---- Triggers ---- */
  document.addEventListener('click', function (event) {
    var target = event.target;
    if (!target || typeof target.closest !== 'function') return;

    var closer = target.closest('[' + CLOSE_ATTR + ']');
    if (closer) {
      var owned = closer.closest('dialog.modal');
      if (owned) {
        event.preventDefault();
        close(owned);
        return;
      }
    }

    var opener = target.closest('[' + OPEN_ATTR + ']');
    if (opener) {
      var dialog = getDialog(opener.getAttribute(OPEN_ATTR));
      if (dialog) {
        event.preventDefault();
        open(dialog);
        return;
      }
    }

    /* The editor-facing convention. A link typed into any richtext setting can open a modal,
       because `href` survives Shopify's richtext sanitiser where a data-* attribute would not.
       preventDefault keeps the hash out of the URL — landing back on the page later with
       #Modal-… still in the address bar would scroll to a dialog that is not open. */
    var link = target.closest('a[href]');
    if (link) {
      var href = link.getAttribute('href') || '';
      var hashAt = href.indexOf(HASH_PREFIX);
      /* Matches "#Modal-x" and "/pages/foo#Modal-x" alike, so the convention works in a nav link
         or a footer link pointing at a modal on another page's markup. */
      if (hashAt === 0 || (hashAt > 0 && href.charAt(hashAt) === '#')) {
        var byHash = getDialog(href.slice(hashAt + HASH_PREFIX.length));
        if (byHash) {
          event.preventDefault();
          open(byHash);
          return;
        }
      }
    }

    /* Nothing above matched, so the click was not on a trigger. A click whose target IS the
       <dialog> landed on the overlay rather than inside .modal__panel — the panel and everything
       in it would have been the target otherwise. That is a click outside, so dismiss. */
    if (target.tagName === 'DIALOG' && target.classList.contains('modal')) {
      close(target);
    }
  });

  /* ESC. The default action closes the dialog outright, which is correct but skips the exit
     animation, so take it over and run the same path a close button does. */
  document.addEventListener(
    'cancel',
    function (event) {
      var dialog = event.target;
      if (!dialog || !dialog.classList || !dialog.classList.contains('modal')) return;
      if (dialog.hasAttribute(CLOSING_ATTR)) return;
      event.preventDefault();
      close(dialog);
    },
    true
  );

  /* The single point where the lock is released: every route out — button, backdrop, ESC, or a
     caller invoking dialog.close() directly — ends here. */
  document.addEventListener(
    'close',
    function (event) {
      var dialog = event.target;
      if (!dialog || !dialog.classList || !dialog.classList.contains('modal')) return;
      dialog.removeAttribute(CLOSING_ATTR);
      unlockScroll();
    },
    true
  );

  /* A section re-rendered by the theme editor while one of its modals is open takes the dialog
     down with it, and no `close` event ever fires — leaving the page permanently unscrollable for
     whoever is editing. Re-checking here clears that. */
  document.addEventListener('shopify:section:load', unlockScroll);
})();
